import { createUseStyles } from 'react-jss';
import { Trans, useTranslation } from 'react-i18next';
import moment from 'moment-timezone';
import { Theme } from '@app/theme';
import {
  Button,
  CenteredButton,
  CopyTextButton,
  InformationBox,
  LabeledIconChip,
  LabeledSection,
  useBoolModalState,
} from '@app/core';
import {
  LinkConfigurationStepConfirmation,
  Participation,
  ParticipationReportReason,
  ParticipationStatus,
  SessionStatus,
  StudyType,
  UnmoderatedTaskSetting,
  useTaskSetting,
} from '@app/api';
import { SummaryDevices } from './SummaryDevices';
import { LinkToMeetingButton } from '../../components/LinkToMeetingButton';
import { InstructionSection } from './InstructionsSection';
import { CancelParticipationBlock } from './CancelParticipationBlock';
import { SuccessScreen } from './SuccessScreen';
import classnames from 'classnames';
import { CompletedSession } from '../../StudyApplication/CompletedSession';
import { FailureScreen } from './FailureScreen';
import { useLoginFromQrCodeUrl } from '../../components/useGenerateToMobileQrcodeUrl';

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.marginBase * 5,
    position: 'relative',
    zIndex: 1,

    width: '100%',
    maxWidth: theme.maxContentWidth.screen,

    margin: 'auto',
    [theme.media.mobile]: {
      boxShadow: 'none',
      maxWidth: '100%',
      gap: theme.marginBase * 7,
      padding: [[theme.marginBase * 3, theme.marginBase * 2]],
    },
  },
  icons: {
    fontSize: theme.iconSize.large,
    paddingLeft: theme.marginBase,
    alignSelf: 'center',
    fontWeight: 500,
  },
  containerInfo: {
    width: '100%',
    background: theme.colors.grayBackground,
    whiteSpace: 'pre-wrap',
    padding: theme.marginBase * 2,
    borderRadius: theme.borderRadius.std,
    ...theme.fonts.caption,
  },
  containerInfoMargin: {
    marginTop: theme.marginBase * 3,
  },
  buttonContainer: {
    marginTop: theme.marginBase * 3,
  },
  buttonContainerCenter: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    marginTop: theme.marginBase * 3,
  },
  completedBox: {
    backgroundColor: theme.colors.white,
    borderRadius: theme.borderRadius.std,
    padding: theme.marginBase * 4,
    margin: 'auto',
    marginTop: theme.marginBase * 4,
    maxWidth: theme.maxContentWidth.screen,
    width: '100%',
  },
}));

interface Props {
  participation: Participation;
  message?: string;
}

const statusStartedList = [ParticipationStatus.IN_PROGRESS];

const statusCompletedList = [
  ParticipationStatus.PARTICIPATED,
  ParticipationStatus.PAYMENT_REQUESTED,
  ParticipationStatus.PROCESSING_PAYMENT,
  ParticipationStatus.PAYED,
];

const statusErrorList = [
  ParticipationStatus.CANCELED,
  ParticipationStatus.NO_SHOW,
];

const statusStartedOrCompletedList = [
  ...statusCompletedList,
  ...statusStartedList,
  ...statusErrorList,
];

export const ParticipationInstructionUnmoderated = ({
  participation,
  message,
}: Props) => {
  const classes = useStyles();
  const { t, i18n } = useTranslation();
  const { session } = participation;
  const { data: taskSetting } = useTaskSetting<UnmoderatedTaskSetting>(
    session?.taskSettingId,
  );
  const error = new URLSearchParams(location.search).get('error');
  const { participant } = participation;
  const [instructionRead, setInstructionRead] = useBoolModalState(
    statusStartedOrCompletedList.includes(participation.status),
  );
  const [tidRead, setTidRead] = useBoolModalState(
    statusStartedOrCompletedList.includes(participation.status),
  );

  const instructions = taskSetting?.studyToolInstructions;

  const timezone = participant?.timezone || moment.tz.guess();
  const deadlineDate = moment.tz(participation.automaticallyRejectAt, timezone);

  const showTid = taskSetting?.linkConfigurationStepConfirmation.includes(
    LinkConfigurationStepConfirmation.MATCHING_PARTICIPANT_ID,
  );
  useLoginFromQrCodeUrl();

  const isFinished = participation.status === ParticipationStatus.PARTICIPATED;

  const getFailureText = (type: 'title' | 'info') => {
    if (participation.status === ParticipationStatus.NO_SHOW) {
      if (
        message &&
        Object.values(ParticipationReportReason).includes(
          message as ParticipationReportReason,
        ) &&
        i18n.exists(`validate.${message}.${type}`)
      ) {
        return `validate.${message}.${type}`;
      }
      return `validate.quality.${type}`;
    }

    if (
      message &&
      ['QuotaFull', 'ScreenOut', 'LegalConsentDeclined'].includes(message)
    ) {
      return `validate.${message}.${type}`;
    }

    return `validate.cancelled.${type}`;
  };

  if (!session || !taskSetting) {
    return null;
  }

  if (
    (session.studyType !== StudyType.MODERATED &&
      session.status !== SessionStatus.LIVE &&
      session.status !== SessionStatus.PAUSED &&
      [ParticipationStatus.IN_PROGRESS, ParticipationStatus.ACCEPTED].includes(
        participation.status,
      )) ||
    error === 'SESSION_FULL'
  ) {
    return (
      <div className={classes.completedBox}>
        <CompletedSession sessionId={session.id} />
      </div>
    );
  }

  return (
    <div className={classes.container}>
      <InformationBox
        variant="success"
        text={t('validate.info.description')}
        title={t('validate.info.title')}
      />
      <InstructionSection title={t('validate.instructions.title', { step: 1 })}>
        {deadlineDate.isValid() && (
          <LabeledIconChip
            label={t('validate.instructions.deadline')}
            chipLabel={deadlineDate.format('LLLL')}
            labelColor="green"
            chipIcon="ri-calendar-event-line"
          />
        )}
        {!!instructions && (
          <LabeledSection label={t('booked.summary.instructions')}>
            <p className={classes.containerInfo}>{instructions}</p>
          </LabeledSection>
        )}
        <SummaryDevices session={session} />
        <CenteredButton
          text={t('validate.instructions.button')}
          small
          className={classes.buttonContainer}
          onClick={setInstructionRead}
        />
      </InstructionSection>
      {showTid && (
        <InstructionSection
          title={t('validate.tid.title', { step: 2 })}
          hidden={!instructionRead}
        >
          <p className={classes.containerInfo}>
            <Trans
              i18nKey="validate.tid.info"
              components={{
                b: <strong />,
              }}
            />
          </p>
          <LabeledSection label={t('validate.tid.labelId')} color="ink">
            <CopyTextButton
              text={participation.trackingId}
              copyText={t('validate.tid.buttonCopy')}
            />
          </LabeledSection>
          <div className={classes.buttonContainerCenter}>
            <Button small text={t('validate.tid.next')} onClick={setTidRead} />
          </div>
        </InstructionSection>
      )}
      <InstructionSection
        title={t('validate.start.title', { step: showTid ? 3 : 2 })}
        hidden={showTid ? !tidRead : !instructionRead}
      >
        <p className={classes.containerInfo}>
          <Trans
            i18nKey="validate.start.info"
            values={{
              time: participation?.duration || session?.duration,
            }}
            components={{
              b: <strong />,
            }}
          />
        </p>
        <div className={classes.buttonContainerCenter}>
          <LinkToMeetingButton
            disabled={
              statusCompletedList.includes(participation.status) ||
              statusErrorList.includes(participation.status)
            }
            participation={participation}
            big
          />
        </div>
      </InstructionSection>
      <InstructionSection
        title={t('validate.inProgress.title', { step: showTid ? 4 : 3 })}
        hidden={!statusStartedOrCompletedList.includes(participation.status)}
      >
        <p className={classes.containerInfo}>
          <Trans
            i18nKey="validate.inProgress.info"
            components={{
              b: <strong />,
            }}
          />
        </p>
      </InstructionSection>
      {message === 'bad-status' && (
        <InformationBox variant="danger" text={t('validate.bad-status')} />
      )}
      <InstructionSection
        hidden={!statusCompletedList.includes(participation.status)}
      >
        <SuccessScreen
          title={
            isFinished
              ? t('validate.finished.title')
              : t('validate.completed.title')
          }
        >
          <p
            className={classnames(
              classes.containerInfo,
              classes.containerInfoMargin,
            )}
          >
            {isFinished
              ? t('validate.finished.info')
              : t('validate.completed.info')}
          </p>
        </SuccessScreen>
      </InstructionSection>
      <InstructionSection
        hidden={!statusErrorList.includes(participation.status)}
      >
        <FailureScreen title={t(getFailureText('title'))}>
          <p
            className={classnames(
              classes.containerInfo,
              classes.containerInfoMargin,
            )}
          >
            <Trans i18nKey={getFailureText('info')} />
          </p>
        </FailureScreen>
      </InstructionSection>
      <CancelParticipationBlock
        isAbandoned
        hidden={
          ![
            ParticipationStatus.IN_PROGRESS,
            ParticipationStatus.ACCEPTED,
          ].includes(participation.status)
        }
      />
    </div>
  );
};
